/**
 * Run a camera's stream test and follow it to the end (ENG-2470).
 *
 * On mount it loads the camera's latest test when it is under 24 hours old
 * (and keeps following it if it is still going). `start()` asks admin for a
 * new test, then polls it every 5 s until it is done or failed, giving up
 * after 6 minutes. A test starts a Job with the connector's image, so the
 * first answer takes 30–120 s; `elapsedMs` ticks every second meanwhile.
 */
import React from "react";
import {
  fetchLatestStreamTest,
  fetchStreamTest,
  startStreamTest,
} from "./streamTestApi";
import { FINISHED_STREAM_TEST, StreamTest } from "./streamTestTypes";

export const STREAM_TEST_POLL_MS = 5000;
export const STREAM_TEST_MAX_POLL_MS = 6 * 60 * 1000;
export const STREAM_TEST_MAX_POLL_ERRORS = 5;
/** An older latest test isn't shown on open. */
export const STREAM_TEST_LATEST_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type StreamTestNotice =
  | {
      kind: "refused";
      code: string;
      detail: string | null;
      retryAt: string | null;
      runId: string | null;
    }
  | { kind: "error"; message: string };

export interface StreamTestState {
  test: StreamTest | null;
  /** `test` is the camera's earlier test, loaded on open. */
  fromLatest: boolean;
  /** From the click until the test finishes. */
  running: boolean;
  /** Since the test started, while it runs. */
  elapsedMs: number;
  loadingLatest: boolean;
  /** The test is switched off on this environment. */
  off: boolean;
  /** An email or partner-event site: nothing to stream. */
  siteType: boolean;
  notice: StreamTestNotice | null;
  start: () => Promise<void>;
  /** Follow another run, e.g. the one a 409 says is already going. */
  follow: (testId: string) => void;
  clearNotice: () => void;
}

const startedAtOf = (test: StreamTest): number => {
  const at = test.created_at ? Date.parse(test.created_at) : NaN;
  return Number.isNaN(at) ? Date.now() : at;
};

export const useStreamTest = (
  cameraId: number | null,
  { loadLatest = true }: { loadLatest?: boolean } = {}
): StreamTestState => {
  // admin's About says whether the test is on (`stream_test_available`), so
  // a 503 here means the setting flipped mid-session: say so in this panel.
  const [off, setOff] = React.useState(false);
  const [test, setTest] = React.useState<StreamTest | null>(null);
  const [fromLatest, setFromLatest] = React.useState(false);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [startedAt, setStartedAt] = React.useState<number | null>(null);
  const [now, setNow] = React.useState(() => Date.now());
  const [starting, setStarting] = React.useState(false);
  const [loadingLatest, setLoadingLatest] = React.useState(false);
  const [siteType, setSiteType] = React.useState(false);
  const [notice, setNotice] = React.useState<StreamTestNotice | null>(null);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A different camera starts from nothing, then shows its latest test.
  React.useEffect(() => {
    setTest(null);
    setFromLatest(false);
    setActiveId(null);
    setNotice(null);
    setSiteType(false);
    if (cameraId == null || !loadLatest) return;
    let cancelled = false;
    setLoadingLatest(true);
    fetchLatestStreamTest(cameraId).then((outcome) => {
      if (cancelled) return;
      setLoadingLatest(false);
      if (outcome.kind !== "ok") return;
      const latest = outcome.data;
      const at = latest.checked_at ?? latest.created_at;
      if (at && Date.now() - Date.parse(at) > STREAM_TEST_LATEST_MAX_AGE_MS) {
        return;
      }
      setTest(latest);
      setFromLatest(true);
      if (!FINISHED_STREAM_TEST.has(latest.status)) {
        setStartedAt(startedAtOf(latest));
        setActiveId(latest.test_id);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [cameraId, loadLatest]);

  // Follow the active test until it finishes.
  React.useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let errors = 0;
    const began = Date.now();
    const stop = (message?: string) => {
      if (message) setNotice({ kind: "error", message });
      setActiveId(null);
    };
    const poll = async () => {
      const outcome = await fetchStreamTest(activeId);
      if (cancelled) return;
      if (outcome.kind === "ok") {
        errors = 0;
        setTest(outcome.data);
        if (FINISHED_STREAM_TEST.has(outcome.data.status)) {
          stop();
          return;
        }
      } else {
        errors += 1;
        if (
          outcome.kind === "not_found" ||
          errors >= STREAM_TEST_MAX_POLL_ERRORS
        ) {
          stop(
            "We lost track of the stream test. Run it again to see the result."
          );
          return;
        }
      }
      if (Date.now() - began >= STREAM_TEST_MAX_POLL_MS) {
        stop(
          "The stream test is taking much longer than it should. Try again in a few minutes."
        );
        return;
      }
      timer = setTimeout(poll, STREAM_TEST_POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeId]);

  // The elapsed-time clock while a test runs.
  const running = starting || activeId != null;
  React.useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [running]);

  const follow = React.useCallback((testId: string) => {
    setNotice(null);
    setFromLatest(false);
    setTest(null);
    setStartedAt(Date.now());
    setActiveId(testId);
  }, []);

  const start = React.useCallback(async () => {
    if (cameraId == null) return;
    setNotice(null);
    setStarting(true);
    setStartedAt(Date.now());
    const outcome = await startStreamTest(cameraId);
    if (!mounted.current) return;
    setStarting(false);
    switch (outcome.kind) {
      case "ok":
        setTest(null);
        setFromLatest(false);
        setActiveId(outcome.data.test_id);
        return;
      case "unavailable":
        if (outcome.siteType) setSiteType(true);
        else setOff(true);
        return;
      case "not_found":
        // The route isn't on this environment yet.
        setOff(true);
        return;
      case "refused":
        setNotice({
          kind: "refused",
          code: outcome.code,
          detail: outcome.detail,
          retryAt: outcome.retryAt,
          runId: outcome.runId,
        });
        return;
      default:
        setNotice({ kind: "error", message: outcome.message });
    }
  }, [cameraId]);

  return {
    test,
    fromLatest,
    running,
    elapsedMs: running && startedAt != null ? Math.max(0, now - startedAt) : 0,
    loadingLatest,
    off,
    siteType,
    notice,
    start,
    follow,
    clearNotice: () => setNotice(null),
  };
};
